import { RiLoader2Fill } from '@remixicon/react';
import { Counter } from './counter';
import { renderIcon, type IconLike } from './icon';
import { tv, type VariantProps } from './lib/tv';

/**
 * Button — kit 67:9574, product variants per spec §4.1.
 *
 * Anatomy: `[icon] gap-2 [label px-4] gap-2 [counter] gap-2 [icon]`, centred, r8.
 * Icon ink as measured in the product screens: `sm` buttons use White/60 even on coloured fills (pixel #90c3ff on
 * #0275ff, header "Saqlash"); `md`/`lg` buttons use the label colour (white — "Rasm yuklash", "Davom etish ›",
 * "Vidjetni o'chirish", 501:10685 / 351:6871).
 */
export const buttonStyles = tv({
  slots: {
    base: [
      'group relative inline-flex shrink-0 cursor-pointer items-center justify-center rounded-8 font-semibold whitespace-nowrap select-none',
      'transition-[background-color,color,box-shadow,opacity] duration-150 ease-standard',
      'disabled:cursor-not-allowed disabled:bg-subtle disabled:text-fg-tertiary disabled:shadow-none',
      'aria-busy:cursor-progress',
    ],
    label: 'min-w-0 truncate px-4',
    icon: 'shrink-0 group-disabled:text-fg-tertiary',
  },
  variants: {
    size: {
      sm: { base: 'h-24 gap-2 px-6 py-4 text-xs', icon: 'size-16' }, // 600 12/16
      md: { base: 'h-32 gap-2 px-10 py-6 text-sm', icon: 'size-16' }, // 600 14/20
      lg: { base: 'h-40 gap-2 px-16 py-10 text-sm', icon: 'size-20' },
    },
    variant: {
      /** bg-accent (#0275ff, user-configurable) — product default ("Saqlash", "Xodim qo'shish"). */
      primary: {
        base: 'bg-accent text-on-accent hover:bg-[color-mix(in_srgb,var(--color-accent)_88%,white)] active:bg-[color-mix(in_srgb,var(--color-accent)_88%,black)]',
      },
      /** bg-accent-2 (#35b5ec) — newer screens 501/556/565 ("Davom etish", "Qo'llash"), spec §9 Q1. */
      'primary-alt': {
        base: 'bg-accent-2 text-on-accent hover:bg-[color-mix(in_srgb,var(--color-accent-2)_88%,white)] active:bg-[color-mix(in_srgb,var(--color-accent-2)_88%,black)]',
      },
      /** White/5 — "Bekor qilish", "Orqaga", "Rasm yuklash". */
      secondary: { base: 'bg-subtle text-fg hover:bg-field active:bg-selected' },
      ghost: { base: 'bg-transparent text-fg hover:bg-hover active:bg-field' },
      outline: { base: 'border border-line bg-transparent text-fg hover:bg-hover active:bg-field' },
      /** White/20 — header "Ko'rish". */
      overlay: { base: 'bg-line-strong text-fg hover:bg-white-300 light:hover:bg-black-300' },
      /** kit Color-Secondary: accent/20 + accent text. */
      soft: {
        base: 'bg-accent-soft text-accent hover:bg-[color-mix(in_srgb,var(--color-accent)_30%,transparent)]',
        icon: 'text-accent',
      },
      /** kit Color-Primary #3576ec — "Saqlash" in device panels (401:8543). */
      control: { base: 'bg-control text-white hover:bg-[color-mix(in_srgb,var(--color-control)_88%,white)]' },
      /** Destructive-Primary #ca3b3d — "Qurilmani o'chirish", "Vidjetni o'chirish". */
      destructive: { base: 'bg-tone-red text-white hover:bg-tone-red-lighter' },
      /** Destructive-Secondary — "Xodimni bloklash". */
      'destructive-soft': { base: 'bg-tone-red-10 text-tone-red hover:bg-tone-red-20', icon: 'text-tone-red' },
      /** #e10202 — "PDF yuklab olish". */
      danger: { base: 'bg-danger text-white hover:bg-[color-mix(in_srgb,var(--color-danger)_88%,white)]' },
      /** Text link in accent-2 ("Ortga qaytish", "Hammasini belgilash"). */
      link: {
        base: 'h-auto bg-transparent px-0 py-0 text-link underline-offset-2 hover:underline disabled:bg-transparent',
        label: 'px-0',
        icon: 'text-link',
      },
    },
    fullWidth: { true: { base: 'w-full' } },
  },
  compoundVariants: [
    { variant: ['primary', 'primary-alt', 'control', 'destructive', 'danger'], size: 'sm', class: { icon: 'text-white-600' } },
    { variant: ['primary', 'primary-alt', 'control', 'destructive', 'danger'], size: ['md', 'lg'], class: { icon: 'text-white' } },
    { variant: ['secondary', 'ghost', 'outline', 'overlay'], size: 'sm', class: { icon: 'text-icon' } },
    { variant: ['secondary', 'ghost', 'outline', 'overlay'], size: ['md', 'lg'], class: { icon: 'text-fg' } },
  ],
  defaultVariants: { size: 'md', variant: 'primary' },
});

type ButtonVariant = NonNullable<VariantProps<typeof buttonStyles>['variant']>;

const COUNTER_TONE: Record<ButtonVariant, 'white' | 'neutral' | 'accent' | 'danger'> = {
  primary: 'white',
  'primary-alt': 'white',
  control: 'white',
  destructive: 'white',
  danger: 'white',
  secondary: 'neutral',
  ghost: 'neutral',
  outline: 'neutral',
  overlay: 'neutral',
  link: 'neutral',
  soft: 'accent',
  'destructive-soft': 'danger',
};

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'>,
    VariantProps<typeof buttonStyles> {
  ref?: React.Ref<HTMLButtonElement>;
  children?: React.ReactNode;
  /** Leading icon — 16 (sm, md) / 20 (lg); White/60 on `sm`, label colour on `md`/`lg` (as measured in the screens). */
  leftIcon?: IconLike;
  /** Trailing icon, e.g. `RiArrowDownSLine` for "Xodim qo'shish ▾". */
  rightIcon?: IconLike;
  /** Counter pill after the label (kit "Counter" boolean). */
  counter?: number | string;
  /** Spinner replaces the left icon; counter and right icon are hidden; clicks are ignored (`aria-busy`). */
  loading?: boolean;
}

export function Button({
  size,
  variant,
  fullWidth,
  leftIcon,
  rightIcon,
  counter,
  loading = false,
  disabled,
  type = 'button',
  className,
  children,
  onClick,
  ref,
  ...rest
}: ButtonProps) {
  const s = buttonStyles({ size, variant, fullWidth });
  const iconCls = s.icon();
  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      disabled={disabled}
      // loading keeps the variant colour (kit "State=Loading") but swallows clicks / submits
      aria-disabled={loading || rest['aria-disabled'] || undefined}
      aria-busy={loading || undefined}
      onClick={loading ? (e) => e.preventDefault() : onClick}
      className={s.base({ class: className })}
    >
      {loading ? <RiLoader2Fill aria-hidden className={`${iconCls} animate-spin`} /> : renderIcon(leftIcon, iconCls)}
      {children != null && children !== false && <span className={s.label()}>{children}</span>}
      {!loading && counter != null && <Counter value={counter} size="sm" tone={COUNTER_TONE[variant ?? 'primary']} />}
      {!loading && renderIcon(rightIcon, iconCls)}
    </button>
  );
}
